import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useTimer } from 'react-timer-hook';
import moment from 'moment';
import RoubleSVG from '../../public/img/redRouble.svg';
import CalendarSVG from '../../public/img/calendar.svg';
import DurationSVG from '../../public/img/duration.svg';
import LocationSVG from '../../public/img/location.svg';
import { Link } from '../styled/Link';
import { Action, City } from '../../models';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { isEventPassed } from '../../utils/event';
import {
  countdownInit,
  formatTimerEvent,
  getBenefit,
  getTitleForTimer,
  revealDate,
  splitPriceByThree,
} from '../../utils';
import { useModal } from '../../providers/modal';
import { YaMapModal } from '../ya-map/YaMapModal';
import { ACTION_TYPES } from '../../models/action';

export function AsideEventBlock({
  item,
  currentCity,
}: {
  item: Action;
  currentCity: City;
}) {
  const theme = useTheme();
  const [progressValue, setProgressValue] = useState(0);
  const modalState = useModal();

  const [timerData, setTimerData] = useState<{
    showTimer: boolean;
    startDate: any;
    endDate: any;
    title: string | null;
  }>({
    showTimer: false,
    startDate: null,
    endDate: null,
    title: null,
  });

  const { seconds, minutes, hours, days, restart } = useTimer({
    expiryTimestamp: timerData.endDate
      ? DateTime.fromISO(timerData.endDate).valueOf()
      : new Date().valueOf(),
    onExpire: () => console.warn('onExpire called'),
  } as any);
  function getMicroDataDate(
    fieldName: 'startDate' | 'endDate',
    isDateUnknown: boolean
  ) {
    if (isDateUnknown) {
      return DateTime.now().toISO() as string;
    }
    if (fieldName === 'startDate' && item.nearest_event) {
      return DateTime.fromFormat(
        item.nearest_event.time.length > 0
          ? [item.nearest_event.start_date, item.nearest_event.time[0]].join(
              ' '
            )
          : item.nearest_event.start_date,
        item.nearest_event.time.length > 0
          ? 'yyyy-LL-dd HH:mm:ss'
          : 'yyyy-LL-dd'
      ).toISO() as string;
    }
    if (fieldName === 'endDate' && item.latest_event) {
      return DateTime.fromFormat(
        item.latest_event.time.length > 0
          ? [item.latest_event.start_date, item.latest_event.time[0]].join(' ')
          : item.latest_event.start_date,
        item.latest_event.time.length > 0 ? 'yyyy-LL-dd HH:mm:ss' : 'yyyy-LL-dd'
      ).toISO() as string;
    }
    return DateTime.now().toISO() as string;
  }
  useEffect(() => {
    const newTimerData = {
      showTimer: false,
      startDate: null,
      endDate: null,
      title: getTitleForTimer(item.countdown_type),
    };

    let dataPrepareForTimer = null;
    const dateList = revealDate(
      item.dates
        .filter(({ quotas }) => {
          if (quotas?.length === 0) return true;
          return quotas?.some(({ quantity }: any) => quantity > 0);
        })
        .map((date) => {
          if (date.time.length > 0) {
            return {
              interval: `${date.start_date} - ${date.end_date}`,
              time: date.time,
            };
          }
          // if there is no time before the end date next day at midnight
          return {
            interval: `${date.start_date} - ${DateTime.fromFormat(
              date.end_date,
              'yyyy-MM-dd'
            )
              .plus({ days: 1 })
              .toFormat('yyyy-MM-dd')}`,
            time: [],
          };
        })
    );

    const dateListWithTz = dateList.map((dateItem: DateTime) => {
      const dateItemString = dateItem.toFormat('yyyy-MM-dd HH:mm:ss');
      return DateTime.fromFormat(dateItemString, 'yyyy-MM-dd HH:mm:ss')
        .setZone(currentCity.tz, { keepLocalTime: true })
        .setZone(DateTime.local().toFormat('z'));
    });

    countdownInit(
      item.countdown_type === 'manual'
        ? [DateTime.fromISO(item.countdown_datetime)]
        : dateListWithTz,
      item.countdown_type,
      (countdown) => {
        dataPrepareForTimer = countdown;
      }
    );

    if (dataPrepareForTimer) {
      newTimerData.showTimer = true;
      newTimerData.endDate =
        moment().valueOf() + (dataPrepareForTimer as any).date;
      newTimerData.startDate = moment(
        moment().valueOf() + (dataPrepareForTimer as any).date
      ).subtract(7, 'day') as any;
    }

    if (newTimerData.showTimer) {
      const a =
        (Number(moment().valueOf()) -
          Number((newTimerData as any).startDate.valueOf())) *
        100;
      const b =
        Number(moment(newTimerData.endDate).valueOf()) -
        Number((newTimerData as any).startDate.valueOf());
      const newProgressVal = Math.round(a / b);
      setProgressValue(newProgressVal);
      restart((newTimerData as any).endDate);
    }

    setTimerData(newTimerData);
  }, [item]);

  return (
    <div
      css={{
        backgroundColor: theme.colors.white,
        borderRadius: 4,
        padding: 30,
        marginBottom: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        fontFamily: 'Golos',
        gap: 8,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0em',
        color: '#333',
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: 20,
        },
      }}
    >
      {timerData.showTimer && (
        <div css={{ width: '100%', marginBottom: 12 }}>
          <div
            css={{
              fontSize: 14,
              fontFamily: 'Golos',
            }}
          >
            <span>{timerData.title}</span>
            <span>&nbsp;&nbsp;&nbsp;</span>
            <span
              css={{
                color: '#F44336',
              }}
            >
              {formatTimerEvent(days, hours, minutes, seconds)}
            </span>
          </div>

          <div
            css={{
              width: '100%',
              height: 4,
              background: '#7D7B7A',
              borderRadius: 2,
              marginTop: 5,
              overflow: 'hidden',
            }}
          >
            <div
              css={{
                height: '100%',
                width: `${progressValue}%`,
                background: '#F44336',
                borderRadius: 2,
                marginLeft: -1,
              }}
            />
          </div>
        </div>
      )}
      {item.type !== ACTION_TYPES.FREE && item.price && item.price !== '' && (
        <div
          title={
            item.instead_price && item.filter_price
              ? `Ваша экономия ${splitPriceByThree(
                  getBenefit(item.instead_price, item.filter_price)
                )}₽`
              : ''
          }
          itemProp='offers'
          itemScope
          itemType='https://schema.org/Offer'
          css={{
            display: 'flex',
            gap: 8,
          }}
        >
          <div css={{ display: 'flex', alignItems: 'flex-start' }}>
            <RoubleSVG css={{ width: 16 }} />
          </div>
          <div>
            <div dangerouslySetInnerHTML={{ __html: item.price }} />
          </div>
          <meta itemProp='price' content={`${item.filter_price || ''}`} />
          <meta itemProp='priceCurrency' content='RUB' />
        </div>
      )}
      {item.type === ACTION_TYPES.FREE && (
        <div
          css={{
            display: 'flex',
            gap: 8,
          }}
        >
          <div css={{ display: 'flex', alignItems: 'flex-start' }}>
            <RoubleSVG css={{ width: 16 }} />
          </div>
          <div>
            <div>Бесплатно</div>
          </div>
        </div>
      )}
      <div
        css={{
          display: 'flex',
          gap: 8,
          maxHeight: 280,
          overflow: 'auto',
          width: '100%',
          '&::-webkit-scrollbar': {
            width: 4,
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundImage: `-webkit-linear-gradient(90deg,
            transparent,
            rgba(0, 0, 0, 0) 0%,
            rgba(244, 67, 54, 0.3) 10%,
            rgba(244, 67, 54, 0.9) 20%,
            rgba(244, 67, 54, 1) 50%,
            rgba(244, 67, 54, 0.9) 80%,
            rgba(244, 67, 54, 0.3) 90%,
            rgba(0, 0, 0, 0) 100%,
            transparent)`,
          },
        }}
      >
        {!isEventPassed(item) && item.dates_txt_detailed.length > 0 ? (
          <>
            <div css={{ display: 'flex', alignItems: 'flex-start' }}>
              <CalendarSVG css={{ width: 16, fill: theme.colors.red }} />
            </div>
            <div
              css={{ display: 'flex', flexDirection: 'column' }}
              dangerouslySetInnerHTML={{
                __html: item.dates_txt_detailed
                  .map((date) =>
                    Object.entries(date).map(([month, datesDays]) => {
                      const daysSeparator = month ? '<br/>' : ' и ';
                      return [month, datesDays.join(daysSeparator)]
                        .filter(Boolean)
                        .join(`<br/>`);
                    })
                  )
                  .join(`<br/>`),
              }}
            />
            <meta
              itemProp='startDate'
              content={getMicroDataDate('startDate', false)}
            />
            {item.with_open_date && (
              <meta
                itemProp='endDate'
                content={getMicroDataDate('endDate', false)}
              />
            )}
          </>
        ) : (
          <span
            css={{
              display: 'flex',
              gap: 8,
            }}
          >
            {item.date_unknown && (
              <>
                <div css={{ display: 'flex', alignItems: 'flex-start' }}>
                  <CalendarSVG css={{ width: 16, fill: theme.colors.red }} />
                </div>
                <p>Дата уточняется</p>
                <meta
                  itemProp='startDate'
                  content={getMicroDataDate('startDate', true)}
                />
                {item.with_open_date && (
                  <meta
                    itemProp='endDate'
                    content={getMicroDataDate('endDate', true)}
                  />
                )}
              </>
            )}
          </span>
        )}
      </div>
      {item.duration && item.duration !== '' && (
        <div
          css={{
            display: 'flex',
            gap: 8,
          }}
        >
          <div css={{ display: 'flex', alignItems: 'flex-start' }}>
            <DurationSVG css={{ width: 16, fill: theme.colors.red }} />
          </div>
          <div itemProp='duration'>{item.duration}</div>
        </div>
      )}
      {item.places.length && (
        <div
          css={{
            display: 'flex',
            gap: 8,
          }}
        >
          <div css={{ display: 'flex', alignItems: 'flex-start' }}>
            <LocationSVG css={{ width: 16, fill: theme.colors.red }} />
          </div>
          <div css={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {item.places &&
              item.places.length > 0 &&
              item.places.map((place) => (
                <div
                  key={place.title}
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 4,
                  }}
                >
                  <Link
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    css={{
                      fontFamily: 'Golos',
                      fontSize: '14',
                      fontWeight: 500,
                      lineHeight: '20px',
                      marginBottom: 10,
                      display: 'inline-block',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dashed',
                      color: theme.colors.red,
                      ':hover': {
                        color: theme.colors.red,
                        textDecorationStyle: 'solid',
                      },
                    }}
                    href={['', 'places', place.slug, ''].join('/')}
                  >
                    {place.title}
                  </Link>
                  {place.addresses.map((address, index) => (
                    <>
                      <div
                        itemProp='location'
                        css={{ lineHeight: '20px' }}
                        key={address}
                      >
                        {address}
                      </div>
                      {place.coordinates_list[index] && (
                        <button
                          key={place.coordinates_list[index]}
                          css={{
                            fontFamily: 'Golos',
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: '20px',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            borderBottom: '1px dashed rgba(0, 0, 0, 0.5)',
                            color: theme.colors.black,
                            ':hover': {
                              borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
                            },
                          }}
                          type='button'
                          onClick={() => {
                            if (modalState) {
                              modalState.setModalState((oldState) => ({
                                ...oldState,
                                show: true,
                                content: (
                                  <YaMapModal
                                    cords={place.coordinates_list[index]}
                                    setModalState={modalState.setModalState}
                                  />
                                ),
                                options: { width: 800 },
                              }));
                            }
                          }}
                        >
                          Показать на карте
                        </button>
                      )}
                    </>
                  ))}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
